import { FunctionComponent, useId, useState } from "react";
import { useFactoryRef } from "@pjs/react-utilities";
import {
    AriaMenuStatus,
    HtmlNotice,
    informationNotice,
    IRadioGroupItem,
    MenuAriaModel,
    Orientation,
    RadioGroup,
    Select,
    TextOption,
    ValidatedInput
} from "@pjs/core-ui";
import { ITemporaryAccessAdmin } from "@pjs/user-domain";
import { Immutable, isEmailLike } from "@pjs/utilities";
import { userUiI18n } from "../../i18n/UserUiI18n.const";
import { InputType } from "../types/InputType";
import { IDefaultAdminMenuItem } from "../interfaces/IDefaultAdminMenuItem";
import { IEmailMenuItem } from "../interfaces/IEmailMenuItem";
import { ISelectUserStepProps } from "../interfaces/ISelectUserStepProps";
import "../styles/select-user-step.css";

const getEmailDataSet = (emails: Immutable<Array<{ key: string; value: string }>>): Array<IEmailMenuItem> => {
    const dataSet: Array<IEmailMenuItem> = [{ value: userUiI18n.getString("temporary_access.steps.select_user.initial_choice"), email: "" }];
    emails.forEach((email) => dataSet.push({ value: email.key, email: email.value }));
    return dataSet;
};

const getDefaultAdminDataSet = (admins: Immutable<Array<ITemporaryAccessAdmin>>): Array<IDefaultAdminMenuItem> => {
    const dataSet: Array<IDefaultAdminMenuItem> = [{ value: userUiI18n.getString("temporary_access.steps.select_user.initial_choice"), adminId: "" }];
    admins.forEach((admin) => dataSet.push({ value: admin.displayName, adminId: admin.id }));
    return dataSet;
};

const getRadioButtonData = (adminId: string, emailId: string): Array<IRadioGroupItem> => {
    return [
        {
            id: adminId,
            value: "admin",
            label: userUiI18n.getString("temporary_access.steps.select_user.admins")
        },
        {
            id: emailId,
            value: "email",
            label: userUiI18n.getString("temporary_access.steps.select_user.email")
        }
    ];
};

const defaultModel: MenuAriaModel = {
    isOpen: false,
    type: AriaMenuStatus.Closed,
    activeItemIndex: 0
};

export const SelectUserStep: FunctionComponent<ISelectUserStepProps> = (props) => {
    const selectUserId = useId();
    const emailId = useId();
    const adminId = useId();

    const emailDataSet = useFactoryRef(getEmailDataSet, props.emails);
    const defaultAdminDataSet = useFactoryRef(getDefaultAdminDataSet, props.admins);

    const [activeRadioButton, setActiveRadioButton] = useState<InputType>("admin");
    const [email, setEmail] = useState<string>("");
    const [selectDefaultAdminModel, setSelectDefaultAdminModel] = useState<MenuAriaModel>(defaultModel);

    const [selectEmailModel, setSelectEmailModel] = useState<MenuAriaModel>(defaultModel);

    const radioButtonData = useFactoryRef(getRadioButtonData, adminId, emailId);

    return (
        <div className="uui-temporary-access__step-container">
            <h3 id={selectUserId} className="uui-temporary-access__title">
                {userUiI18n.getString("temporary_access.steps.select_user.title", { stepNumber: "2" })}
            </h3>
            <p className="uui-temporary-access__description">{userUiI18n.getString("temporary_access.steps.select_user.description")}</p>

            {props.emails.length === 1 && (
                <HtmlNotice
                    ariaLabel={userUiI18n.getString("temporary_access.steps.select_user.a11y.set_by_admin", { adminName: props.emails[0].key })}
                    type={informationNotice}
                    content={userUiI18n.getString("temporary_access.steps.select_user.set_by_admin", { adminName: props.emails[0].key })}
                />
            )}

            {props.emails.length > 1 && (
                <div className="uui-select-user-step__select-container">
                    <Select
                        dataHook="email-selector"
                        model={selectEmailModel}
                        onModelChange={(model) => {
                            if (model.activeItemIndex !== selectEmailModel.activeItemIndex) {
                                props.onEmailChange(emailDataSet[model.activeItemIndex].email, model.activeItemIndex !== 0);
                            }
                            setSelectEmailModel(model);
                        }}
                        dataSet={emailDataSet}
                        labelId={selectUserId}
                        renderOption={TextOption}
                        required={true}
                    />
                </div>
            )}

            {props.emails.length === 0 && (
                <div>
                    <RadioGroup
                        items={radioButtonData}
                        value={activeRadioButton}
                        orientation={Orientation.Horizontal}
                        label={userUiI18n.getString("temporary_access.steps.select_user.type")}
                        onChange={(value) => {
                            props.onInputTypeChange(value as InputType);
                            setActiveRadioButton(value as InputType);
                        }}
                    />
                    {activeRadioButton === "admin" && (
                        <div className="uui-select-user-step__select-container">
                            <Select
                                dataHook="admin-selector"
                                model={selectDefaultAdminModel}
                                onModelChange={(model) => {
                                    if (model.activeItemIndex !== selectDefaultAdminModel.activeItemIndex) {
                                        const index = model.activeItemIndex;
                                        props.onAdminChange(defaultAdminDataSet[index].adminId, index);
                                    }
                                    setSelectDefaultAdminModel(model);
                                }}
                                dataSet={defaultAdminDataSet}
                                labelId={selectUserId}
                                renderOption={TextOption}
                                required={true}
                            />
                        </div>
                    )}
                    {activeRadioButton === "email" && (
                        <div className="uui-select-user-step__input-container">
                            <ValidatedInput
                                ariaLabel={userUiI18n.getString("temporary_access.steps.select_user.a11y.email")}
                                value={email}
                                isValid={isEmailLike}
                                onInvalidInput={(value) => {
                                    props.onEmailChange(value, false);
                                    setEmail(value);
                                }}
                                onValidInput={(value) => {
                                    props.onEmailChange(value, true);
                                    setEmail(value);
                                }}
                                required={true}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
